/**
 * Entry point for the public track pages at /tracks/<slug>.
 *
 * These pages are NOT the SPA. `scripts/build-track-pages.mjs` prerenders the
 * whole readable page — heading, layout SVG, the numbers, the prose, the links —
 * into static HTML, and this file only mounts the two interactive islands into
 * boxes that page reserved. That split is the point: a search engine (and a
 * visitor with JavaScript off, and any visitor before this chunk lands) gets a
 * complete, useful track map with no JavaScript at all.
 *
 * Nothing here is authenticated. Every fetch goes to /api/public/*, which the
 * page can reach with no account and which has already stripped the driver, the
 * car and the session id from what it returns.
 *
 * Note the deliberate absence of ThemeProvider. Its effect calls applyTheme(),
 * which writes the *app's* palette as inline styles on <html> — beating the
 * page's own stylesheet, repainting a marketing page in app colors on
 * hydration, and doing it from whatever theme the visitor last chose in the
 * logged-in app. The page owns its colors in a :root block instead, using the
 * same variable names the components read, and useTheme()'s context-free
 * fallback supplies the chart palette.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary.jsx';
import TrackMapIsland from './components/Public/TrackMapIsland.jsx';
import TrackLapIsland from './components/Public/TrackLapIsland.jsx';
import { getPublicTrack } from './services/api.js';

function slugFromLocation() {
  // Stamped into the page by the prerender script — the production answer.
  if (window.__TRACK_SLUG__) return window.__TRACK_SLUG__;
  const match = window.location.pathname.match(/\/tracks\/([^/?#]+)/);
  if (match) return decodeURIComponent(match[1]).replace(/\.html$/, '');
  // `vite dev`, where main.jsx bounces /tracks/<slug> to /track.html?slug=…
  return new URLSearchParams(window.location.search).get('slug');
}

function mount(el, children) {
  createRoot(el).render(
    <StrictMode>
      <ErrorBoundary>{children}</ErrorBoundary>
    </StrictMode>,
  );
}

async function start() {
  const slug = slugFromLocation();
  if (!slug) return;

  const mapHost = document.getElementById('track-map-mount');
  const lapHost = document.getElementById('track-lap-mount');
  if (!mapHost && !lapHost) return;

  // Read the prerendered SVG out before React claims the node, so the map
  // island can keep showing it until the satellite view is actually wanted.
  const staticMap = mapHost ? mapHost.innerHTML : '';

  let data;
  try {
    data = await getPublicTrack(slug);
  } catch {
    // The static page is already complete and correct. A failed enhancement
    // fetch should leave it exactly as it is rather than replace working
    // content with an error.
    return;
  }
  if (!data?.featured_lap?.points?.length) return;

  // SpeedTrace and the 3D cursor sync both consume the standard lap-trace
  // shape, so assemble one. There is no lap_number: which lap of a session this
  // was is not something a public page needs to say.
  const trace = {
    lap_index: 0,
    lap_time_str: data.featured_lap.lap_time_str,
    lap_time_seconds: data.featured_lap.lap_time_seconds,
    start_time: 0,
    points: data.featured_lap.points,
  };

  if (mapHost) {
    mount(mapHost, <TrackMapIsland trace={trace} staticMap={staticMap} />);
  }
  if (lapHost) {
    mount(lapHost, (
      <TrackLapIsland
        slug={slug}
        trace={trace}
        displayName={data.display_name}
      />
    ));
  }
}

start();
